import { logger } from "../../utils/logger";
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Card, CardContent,
  Button, Grid, Paper, Chip, CircularProgress, Alert, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip
} from '@mui/material';
import {
  AccountBalance as QuickBooksIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PageHeader from '../../components/PageHeader';
import CompanySelector from '../../components/CompanySelector';
import { companyService } from '../../services/companyService';
import platformService from '../../services/platformService';
import { api } from '../../services/api';
import { mapErrorToUserMessage } from '../../utils/errorMapping';
import { useAuth } from '../../contexts/AuthContext';

const QuickBooksIntegration = () => {
  const { user } = useAuth(); // Get current user with company_id
  const [companies, setCompanies] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // New state for success messages
  
  // Helper to ensure error is always a string
  const getErrorMessage = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    if (err?.message) return String(err.message);
    return 'An error occurred';
  };
  const [quickbooksData, setQuickbooksData] = useState({
    companies: [],
    customers: [],
    transactions: [],
    taxRates: []
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const breadcrumbs = [
    { label: 'Platform Integrations', url: '/platforms/integrations' },
    { label: 'QuickBooks Integration' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const companiesData = await companyService.listCompanies();
        setCompanies(companiesData);
        // const defaultCompanyId = companiesData.length > 0 ? companiesData[0].id : null;
        // setSelectedCompanyId(defaultCompanyId);
        await loadIntegrations();
      } catch (err) {
        setError(getErrorMessage(err));
        logger.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (code && state) {
      handleOAuthCallback(code, state);
    } else if (error) {
      setError(getErrorMessage(error));
      enqueueSnackbar(`QuickBooks authentication failed: ${getErrorMessage(error)}`, { variant: 'error' });
    }
  }, [location]);

  const loadIntegrations = async () => {
    try {
      // Get the current user's company ID
      const currentCompanyId = user?.primary_company_id;
      logger.log(`[QB_LOAD_INTEGRATIONS] Loading integrations for company_id: ${currentCompanyId}`);
      
      if (!currentCompanyId) {
        logger.warn('[QB_LOAD_INTEGRATIONS] No company_id found for user, cannot load integrations');
        setIntegrations([]);
        return;
      }
      
      // Fetch integrations filtered by current company
      const integrationsData = await platformService.listIntegrations(currentCompanyId);
      logger.log(`[QB_LOAD_INTEGRATIONS] Received ${integrationsData.length} integrations from API`);
      
      // Filter for QuickBooks integrations only, checking both platform.name and platform_name
      const quickbooksIntegrations = integrationsData.filter(integration => {
        const platformName = integration.platform_name?.toLowerCase() || 
                            integration.platform?.name?.toLowerCase();
        const isQuickBooks = platformName === 'quickbooks';
        logger.log(`[QB_LOAD_INTEGRATIONS] Integration ${integration.id}: platform_name=${integration.platform_name}, isQuickBooks=${isQuickBooks}, company_id=${integration.company_id}`);
        return isQuickBooks && integration.company_id === currentCompanyId;
      });
      
      logger.log(`[QB_LOAD_INTEGRATIONS] Filtered to ${quickbooksIntegrations.length} QuickBooks integrations for company ${currentCompanyId}`);
      setIntegrations(quickbooksIntegrations);
      
      // Log connection status for debugging
      quickbooksIntegrations.forEach(integration => {
        logger.log(`QuickBooks Integration ${integration.id}:`, {
          company_id: integration.company_id,
          is_active: integration.is_active,
          has_access_token: !!integration.access_token,
          has_refresh_token: !!integration.refresh_token,
          token_expires_at: integration.token_expires_at,
          platform_name: integration.platform_name,
          store_id: integration.store_id,
          transaction_stats: integration.transaction_stats,
          recent_transactions: integration.recent_transactions
        });
      });
    } catch (error) {
      logger.error('Error loading integrations:', error);
      setError(getErrorMessage(error));
    }
  };

  const handleOAuthCallback = async (code, state) => {
    try {
      setConnecting(true);
      // Call backend to exchange code for tokens using authenticated API
      const response = await api.post('/api/v1/platforms/quickbooks/oauth/callback', {
        code,
        state,
        // company_id: selectedCompanyId // This line is removed
      });

      const result = response.data;
      enqueueSnackbar('QuickBooks connected successfully!', { variant: 'success' });
      
      // Wait a moment for the backend to process the tokens
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Refresh integrations to show updated connection status
      await loadIntegrations();
      
      // Clear URL parameters
      navigate('/platforms/quickbooks-integration', { replace: true });
    } catch (err) {
      logger.error('[QB_OAUTH_CALLBACK] Error:', err);
      const errorMessage = mapErrorToUserMessage(err);
      setError(errorMessage);
      enqueueSnackbar(`Failed to connect QuickBooks: ${errorMessage}`, { variant: 'error' });
      
      // Clear URL parameters even on error
      navigate('/platforms/quickbooks-integration', { replace: true });
    } finally {
      setConnecting(false);
    }
  };

  // Helper to check for existing QuickBooks integration for a company
  const hasExistingQuickBooksIntegration = (companyId) => {
    return integrations.some(
      (integration) =>
        integration.company_id === companyId &&
        (integration.platform?.name?.toLowerCase() === 'quickbooks' || integration.platform_id === 3) // assuming 3 is QuickBooks
    );
  };

  const handleConnectQuickBooks = async () => {
    // if (!selectedCompanyId) { // This line is removed
    //   enqueueSnackbar('Please select a company first', { variant: 'warning' }); // This line is removed
    //   return; // This line is removed
    // } // This line is removed

    try {
      setConnecting(true);
      const status = getConnectionStatus();
      const shouldForceReconnect = status.status === 'connected' || status.status === 'expired';
      
      logger.log(`[QB_CONNECT] Connection status: ${status.status}, force_reconnect: ${shouldForceReconnect}`);
      
      // Get OAuth URL from backend using authenticated API
      const response = await api.post('/api/v1/platforms/quickbooks/oauth/authorize', {
        // company_id: selectedCompanyId, // This line is removed
        redirect_uri: `${window.location.origin}/quickbooks/callback`,
        force_reconnect: shouldForceReconnect
      });

      const { auth_url } = response.data;
      logger.log(`[QB_CONNECT] Received auth_url, redirecting to QuickBooks OAuth`);
      // Redirect to QuickBooks OAuth
      window.location.href = auth_url;
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      enqueueSnackbar(`Failed to start QuickBooks connection: ${mapErrorToUserMessage(err)}`, { variant: 'error' });
    } finally {
      setConnecting(false);
    }
  };

  const handleSyncData = async (integrationId) => {
    try {
      setSyncLoading(true);
      logger.log(`[UI] Syncing QuickBooks data for integration ${integrationId}`);
      // Default last 90 days when triggering manual sync from this page
      const end = new Date();
      const start = new Date(end);
      start.setDate(end.getDate() - 90);
      const result = await platformService.syncTransactions(integrationId, { 
        startDate: start.toISOString().split('T')[0], 
        endDate: end.toISOString().split('T')[0], 
        background: true 
      });
      logger.log('[UI] Sync result:', result);
      enqueueSnackbar('QuickBooks data sync started. It may take a few minutes to complete.', { variant: 'success' });
      // Refresh integrations to show updated data
      await loadIntegrations();
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      enqueueSnackbar(`Failed to sync QuickBooks data: ${mapErrorToUserMessage(err)}`, { variant: 'error' });
      logger.error('[UI] Sync error:', err);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleTestConnection = async (integrationId) => {
    try {
      setLoading(true);
      logger.log(`[UI] Testing QuickBooks connection for integration ${integrationId}`);
      const result = await platformService.verifyCredentials(integrationId);
      logger.log('[UI] Test connection result:', result);
      if (result.success) {
        setSuccess('QuickBooks connection is working!');
        // Refresh integrations to show updated status
        await loadIntegrations();
      } else {
        enqueueSnackbar('QuickBooks connection test failed', { variant: 'error' });
      }
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      enqueueSnackbar(`Failed to test QuickBooks connection: ${mapErrorToUserMessage(err)}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = async (newCompanyId) => {
    // setSelectedCompanyId(newCompanyId); // This line is removed
    await loadIntegrations();
  };

  const handleDisconnect = async (integrationId) => {
    try {
      setLoading(true);
      // Update integration to remove tokens
      await platformService.updateIntegration(integrationId, {
        access_token: null,
        refresh_token: null,
        token_expires_at: null,
        is_active: false
      });
      
      enqueueSnackbar('QuickBooks disconnected successfully!', { variant: 'success' });
      await loadIntegrations();
    } catch (err) {
      setError(mapErrorToUserMessage(err));
      enqueueSnackbar(`Failed to disconnect QuickBooks: ${mapErrorToUserMessage(err)}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  // Fix getIntegrationStatus to show 'Connected' if access_token is present and is_active
  const getIntegrationStatus = (integration) => {
    if (!integration.is_active) {
      return { color: 'error', label: 'Inactive' };
    }
    if (integration.access_token) {
      return { color: 'success', label: 'Connected' };
    }
    return { color: 'warning', label: 'Not Connected' };
  };

  const isConnected = integrations.some(integration => 
    integration.is_active && integration.access_token && 
    (integration.platform_name?.toLowerCase() === 'quickbooks' || 
     integration.platform?.name?.toLowerCase() === 'quickbooks')
  );

  const getConnectionStatus = () => {
    const quickbooksIntegration = integrations.find(integration => 
      integration.platform_name?.toLowerCase() === 'quickbooks' ||
      integration.platform?.name?.toLowerCase() === 'quickbooks'
    );
    
    if (!quickbooksIntegration) {
      return { status: 'not_connected', label: 'Not Connected', color: 'warning' };
    }
    
    if (!quickbooksIntegration.is_active) {
      return { status: 'inactive', label: 'Inactive', color: 'error' };
    }
    
    if (!quickbooksIntegration.access_token) {
      return { status: 'no_token', label: 'No Token', color: 'warning' };
    }
    
    if (quickbooksIntegration.token_expires_at) {
      const expiresAt = new Date(quickbooksIntegration.token_expires_at);
      if (expiresAt < new Date()) {
        return { status: 'expired', label: 'Token Expired', color: 'error' };
      }
    }
    
    return { status: 'connected', label: 'Connected', color: 'success' };
  };

  const getConnectButtonText = () => {
    if (connecting) return 'Connecting...';
    const status = getConnectionStatus();
    if (status.status === 'connected') return 'Reconnect QuickBooks';
    if (status.status === 'expired') return 'Token Expired - Reconnect';
    if (status.status === 'no_token') return 'No Token - Connect';
    return 'Connect QuickBooks';
  };

  const getConnectButtonColor = () => {
    const status = getConnectionStatus();
    if (status.status === 'connected') return 'primary'; // Changed from 'success' to 'primary' to indicate action is available
    if (status.status === 'expired') return 'warning'; // Changed from 'error' to 'warning' to be less alarming
    if (status.status === 'no_token') return 'warning';
    return 'primary';
  };

  return (
    <Container maxWidth="xl">
      <PageHeader 
        title="QuickBooks Integration" 
        breadcrumbs={breadcrumbs}
        actionButton={
          <Button 
            variant="contained" 
            color={getConnectButtonColor()}
            startIcon={isConnected ? <CheckCircleIcon /> : <QuickBooksIcon />} 
            onClick={handleConnectQuickBooks}
            disabled={connecting || !integrations.length} // Disable if no integrations found
          >
            {getConnectButtonText()}
          </Button>
        }
      />
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Connect your QuickBooks account to automatically sync companies, customers, and sales data
        </Typography>
        
        {/* Quick Actions */}
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={() => loadIntegrations()}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<OpenInNewIcon />} 
            onClick={() => navigate('/platforms/integrations')}
          >
            All Integrations
          </Button>
        </Stack>

        {/* Company Selector */}
        {/* <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <CompanySelector
            selectedCompanyId={selectedCompanyId}
            onCompanyChange={handleCompanyChange}
            label="Company"
          />
        </Box> */}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <QuickBooksIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{integrations.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    QuickBooks Connections
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {integrations.filter(i => i.is_active && i.access_token).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Connections
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {integrations.reduce((sum, i) => sum + (i.transaction_stats?.total_transactions || 0), 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Transactions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssessmentIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {integrations.reduce((sum, i) => sum + (i.transaction_stats?.tax_calculated || 0), 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tax Calculated
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {typeof error === 'string' ? error : getErrorMessage(error)}
        </Alert>
      )}

      {/* Success Display */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* QuickBooks Integrations */}
      {!loading && (
        <Paper elevation={1}>
          {integrations.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <QuickBooksIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No QuickBooks connections
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect your first QuickBooks account to start syncing data
              </Typography>
              <Button 
                variant="contained" 
                color={getConnectButtonColor()}
                startIcon={isConnected ? <CheckCircleIcon /> : <QuickBooksIcon />} 
                onClick={handleConnectQuickBooks}
                disabled={connecting || !integrations.length} // Disable if no integrations found
              >
                {getConnectButtonText()}
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Transactions</TableCell>
                    <TableCell>Last Sync</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {integrations.map((integration) => {
                    const status = getIntegrationStatus(integration);
                    const stats = integration.transaction_stats || {};
                    
                    return (
                      <TableRow key={integration.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {integration.settings?.company_name || 'QuickBooks Company'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            icon={status.color === 'success' ? <CheckCircleIcon /> : <ErrorIcon />} 
                            label={status.label} 
                            color={status.color} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {stats.total_transactions || 0} total
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {stats.tax_calculated || 0} calculated
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {integration.last_sync_at 
                              ? new Date(integration.last_sync_at).toLocaleString() 
                              : 'Never'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Test Connection">
                              <IconButton
                                size="small"
                                onClick={() => handleTestConnection(integration.id)}
                                disabled={loading}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sync Data">
                              <IconButton
                                size="small"
                                onClick={() => handleSyncData(integration.id)}
                                disabled={syncLoading}
                              >
                                <SyncIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Transactions">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/platforms/transactions/${integration.id}`)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            {status.label === 'Connected' && (
                              <Tooltip title="Disconnect QuickBooks">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDisconnect(integration.id)}
                                  disabled={loading}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default QuickBooksIntegration; 